import { Building2, Banknote, ClipboardCheck, Loader2 } from "lucide-react";

interface ApprovalLogProps {
  bankClosingAmount: number;
  physicalCashCount: number;
  confirmBankBalance: string;
  confirmCash: string;
  remarks: string;
  difference: number | null;
  isSubmitting: boolean;
  onConfirmBankBalanceChange: (v: string) => void;
  onConfirmCashChange: (v: string) => void;
  onRemarksChange: (v: string) => void;
  onSubmit: () => void;
}

export function ApprovalLog({
  bankClosingAmount,
  physicalCashCount,
  confirmBankBalance,
  confirmCash,
  remarks,
  difference,
  isSubmitting,
  onConfirmBankBalanceChange,
  onConfirmCashChange,
  onRemarksChange,
  onSubmit,
}: ApprovalLogProps) {
  const differenceLabel =
    difference === null
      ? null
      : difference < 0
        ? `OMR ${Math.abs(difference).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} (Shortage)`
        : difference > 0
          ? `+OMR ${difference.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} (Surplus)`
          : "OMR 0 (Balanced)";

  const differenceColor =
    difference === null
      ? "text-[#8A9BB5]"
      : difference < 0
        ? "text-[#D14343]"
        : difference > 0
          ? "text-[#D97706]"
          : "text-[#0E9F6E]";

  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] p-5 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-[10px] bg-[#EEF3FF] flex items-center justify-center">
          <ClipboardCheck className="w-4 h-4 text-[#1A5EF9]" />
        </div>
        <h3 className="text-base font-semibold text-[#21314A]">
          Close Session
        </h3>
      </div>

      {/* Expected amounts */}
      <div className="rounded-[14px] bg-[#F5F8FD] border border-[#E5ECF8] p-4 space-y-3">
        <p className="text-xs font-semibold text-[#7D8998] uppercase tracking-wide">
          Expected Balances
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-[#1A5EF9]" />
            <span className="text-sm text-[#52627C]">Bank</span>
          </div>
          <span className="text-sm font-bold text-[#21314A]">
            OMR {bankClosingAmount.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Banknote className="w-3.5 h-3.5 text-[#0E9F6E]" />
            <span className="text-sm text-[#52627C]">Cash</span>
          </div>
          <span className="text-sm font-bold text-[#21314A]">
            OMR {physicalCashCount.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
          </span>
        </div>
      </div>

      {/* Confirm inputs */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-[#7D8998] uppercase tracking-wide">
          Actual Count
        </p>
        <div>
          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
            Confirm Bank Balance
          </label>
          <input
            type="number"
            step="any"
            value={confirmBankBalance}
            onChange={(e) => onConfirmBankBalanceChange(e.target.value)}
            placeholder="0.000"
            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
            Confirm Cash
          </label>
          <input
            type="number"
            step="any"
            value={confirmCash}
            onChange={(e) => onConfirmCashChange(e.target.value)}
            placeholder="0.000"
            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9]"
          />
        </div>
      </div>

      {/* Difference */}
      <div className="flex items-center justify-between rounded-[12px] border border-[#E5ECF8] bg-[#FAFCFF] px-4 py-3">
        <span className="text-sm font-medium text-[#52627C]">Variance</span>
        <span className={`text-sm font-bold ${differenceColor}`}>
          {differenceLabel ?? "—"}
        </span>
      </div>

      {/* Remarks */}
      <div>
        <label className="text-xs font-medium text-[#52627C] mb-1.5 block">
          Remarks <span className="text-red-400">*</span>
        </label>
        <textarea
          value={remarks}
          onChange={(e) => onRemarksChange(e.target.value)}
          placeholder="Enter closing remarks..."
          rows={3}
          className="w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 py-3 text-sm text-[#23324D] outline-none transition focus:border-[#1A5EF9] resize-none"
        />
      </div>

      {/* Submit */}
      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className="h-12 w-full rounded-[14px] bg-[#1A5EF9] text-white font-semibold text-sm transition-all hover:bg-[#0E4FDE] hover:shadow-[0_8px_20px_rgba(26,94,249,0.3)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting && <Loader2 className="animate-spin w-4 h-4" />}
        {isSubmitting ? "Closing Session..." : "Close & Submit"}
      </button>
    </div>
  );
}
